import { useContext, useEffect, useMemo, useState, memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Divider,
    Paper,
    Skeleton,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';

import { Helmet } from 'react-helmet-async';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import {
    setFilterDomainPLA,
    setFilterDate,
    setFilterCompare,
    setFilterDateCompare,
    setFilterKeyword,
} from 'src/context/reducers/filter-settings/filterSlice';
import countriesJson from 'src/utils/countries.json';
import { subDays } from 'date-fns';
import AdsLoadingScreen from 'src/components/competitive-intelligence/components/AdsLoadingScreen';
import { CountryFilter } from 'src/components/competitive-intelligence/components/CountryFilter';
import { useCountryData } from 'src/components/competitive-intelligence/hooks/useCountryData';
import { useAdsData } from 'src/components/competitive-intelligence/hooks/useAdsData';
import { DomainItem, AdItem, PlaItem, TextAdItem } from 'src/components/competitive-intelligence/types';
import { CompetitiveIntelligenceContext } from 'src/components/competitive-intelligence/components/CompetitiveIntelligenceProvider';
import AdsFiltersDrawer from 'src/components/competitive-intelligence/components/AdsFiltersDrawer';
import NewLeftDomains from 'src/components/competitive-intelligence/components/LeftDomains';
import NewPlaCarousel from 'src/components/competitive-intelligence/components/PlaCarousel';
import NewTextAdsList from 'src/components/competitive-intelligence/components/TextAdsList';
import NewRightPanelAds from 'src/components/competitive-intelligence/components/RightPanelAds';
import { filterPlaItems, filterTextAds } from 'src/components/competitive-intelligence/utils/filters';
import { sortPlaItems, sortTextAds } from 'src/components/competitive-intelligence/utils/sort';
import { searchPla, searchTextAds, searchRightPanel } from 'src/components/competitive-intelligence/utils/search';
import TopAttackedSearchTerms from 'src/components/competitive-intelligence/components/TopAttackedSearchTerms';
import { normalizeDomain } from 'src/components/competitive-intelligence/utils/domain';
import Iconify from 'src/components/iconify';


// Shared module-level constants (stable references)
// These avoid recreating empty arrays in render and keep dependencies simple
const EMPTY_STRING_ARRAY = [] as string[];
const EMPTY_NUM_RANGE = [] as number[];

// Generic data pipeline: domain filter → store filters → search → sort
// Keeps transformation steps consistent and easy to follow for each dataset
function runPipeline<T>(args: {
    items: T[];
    domain?: string | null;
    filterFn: (items: T[]) => T[];
    searchFn: (items: T[]) => T[];
    sortFn: (items: T[]) => T[];
}): T[] {
    const { items, domain, filterFn, searchFn, sortFn } = args;
    const byDomain = domain ? (items as any[]).filter((it) => (it as any).domain === domain) as T[] : items;
    const filtered = filterFn(byDomain);
    const searched = searchFn(filtered);
    return sortFn(searched);
}

/*
  Ads page logical flow (high-level):
  1) Read workspace/context (country), Redux filters, and fetch countries + ads data.
  2) Normalize filters and memoize derived values (e.g., brand mode, flagged domains).
  3) Build domain list for the left panel and keep selected domain in sync with Drawer.
  4) For each dataset (PLA, Text, RightPanel), run the same pipeline: domain → filters → search → sort.
  5) Compute visibility flags to decide layout (PLA carousel, Text list, Right panel grid).
  6) Render: country selector, left domains, content (PLA/Text/Right) or empty state; show skeleton while loading.
*/

// Memoized child components to avoid unnecessary re-renders
const MemoLeftDomains = memo(NewLeftDomains, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.selected === nextProps.selected &&
    prevProps.adType === nextProps.adType &&
    prevProps.selectedCountry === nextProps.selectedCountry);


const MemoRightPanelAds = memo(NewRightPanelAds, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.columns === nextProps.columns &&
    prevProps.pageSize === nextProps.pageSize &&
    prevProps.showBrandChip === nextProps.showBrandChip &&
    prevProps.totalCount === nextProps.totalCount
);

const MemoPlaCarousel = memo(NewPlaCarousel, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.layout === nextProps.layout &&
    prevProps.showBrandChip === nextProps.showBrandChip
);

const MemoTextAdsList = memo(NewTextAdsList, (prevProps, nextProps) =>
    prevProps.items === nextProps.items &&
    prevProps.pageSize === nextProps.pageSize &&
    prevProps.showBrandChip === nextProps.showBrandChip
);

export default function Ads() {
    const { t } = useTranslation();
    const [selectedDomain, setSelectedDomain] = useState<string>('');
    const deepLinkedDomainRef = useRef<string | null>(null);
    const ciContext = useContext(CompetitiveIntelligenceContext);
    const dispatch = useDispatch();
    const { adType, searchQuery, sortBy, sortDirection } = useSelector((state: any) => state.competitiveIntelligenceAdsUi || { adType: 'ALL', searchQuery: '', sortBy: 'Freq.', sortDirection: 'desc' });
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);

    // Use selectedCountry from context instead of local state to avoid infinite loops
    const selectedCountry = ciContext?.selectedCountry || null;

    // Fetch countries data
    const { countriesData, isLoading: isLoadingCountries } = useCountryData();
    const location = useLocation();

    // Fetch ads data based on effective selected country
    const { AdItems, domainItems, keywordItems, isFetchingKeywords, isLoading: isLoadingAdsData } = useAdsData(selectedCountry, selectedDomain);

    // Local Filters Drawer (simplified version adapted from GlobalPlaAds)
    const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

    const routePathname = location.pathname;

    // Deep link support on Ads: ?country=FR and optional ?domain=example.com
    useEffect(() => {
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country');
        if (codeParam) {
            const mappedName = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
            if (mappedName && countriesData.some((c) => c.country_name === mappedName)) {
                try {
                    ciContext?.setSelectedCountry?.(mappedName);
                    if (workspace?.id) localStorage.setItem(`ci.selectedCountry.${workspace.id}`, mappedName);
                } catch { console.error("unable to get ci.selectedCountry from localStorage") }
            }
        }
        const domainParam = params.get('domain');
        if (domainParam && domainParam.trim()) {
            const d = domainParam.trim();
            deepLinkedDomainRef.current = d;
            setSelectedDomain(d);
        }
    }, [location.search, countriesData, ciContext, workspace?.id]);


    // Listen to global header filter button to open the page-specific filters (Ads page)
    useEffect(() => {
        const handler = () => {
            setFiltersDrawerOpen(true);
        };
        window.addEventListener('openPageFiltersDrawer', handler as any);
        return () => window.removeEventListener('openPageFiltersDrawer', handler as any);
    }, []);

    // Close the local filters drawer when navigating to another route (avoid leaking state from other pages)
    useEffect(() => {
        setFiltersDrawerOpen(false);
    }, [routePathname]);

    // Derive filter values from Redux store (stable defaults)
    const domainsPLA = useMemo(() => ((filterStore as any)?.domainsPLA ?? EMPTY_STRING_ARRAY) as string[], [filterStore]);
    const advertizersPLA = useMemo(() => ((filterStore as any)?.advertizersPLA ?? EMPTY_STRING_ARRAY) as string[], [filterStore]);
    const impressionSharePLA = useMemo(() => ((filterStore as any)?.impressionSharePLA ?? EMPTY_NUM_RANGE) as number[], [filterStore]);
    const averagePositionPLA = useMemo(() => ((filterStore as any)?.averagePositionPLA ?? EMPTY_NUM_RANGE) as number[], [filterStore]);
    const pricingRangePLA = useMemo(() => ((filterStore as any)?.pricingRangePLA ?? EMPTY_NUM_RANGE) as number[], [filterStore]);

    const applyStoreFiltersToPla = useCallback((items: PlaItem[]): PlaItem[] => filterPlaItems(items, {
        domainsPLA,
        brandsPLA: (filterStore as any)?.brandsPLA,
        advertizersPLA,
        impressionSharePLA,
        averagePositionPLA,
        pricingRangePLA,
    }), [domainsPLA, advertizersPLA, impressionSharePLA, averagePositionPLA, pricingRangePLA, filterStore]);

    const applyStoreFiltersToText = useCallback((items: TextAdItem[]): TextAdItem[] => filterTextAds(items, {
        domainsPLA,
        brandsPLA: (filterStore as any)?.brandsPLA,
        advertizersPLA,
        impressionSharePLA,
        averagePositionPLA,
    }), [domainsPLA, advertizersPLA, impressionSharePLA, averagePositionPLA, filterStore]);

    const applyStoreFiltersToRightPanel = useCallback((items: PlaItem[]): PlaItem[] => applyStoreFiltersToPla(items), [applyStoreFiltersToPla]);

    // Run the dataset pipeline: domain → filters → search → sort
    const plaItems: PlaItem[] = useMemo(() => AdItems
        .filter((it: any) => (it.adType || 'PLA') === 'PLA')
        .map((it: any) => ({
            title: it.title,
            price: typeof it.price === 'string' ? it.price : String(it.price ?? ''),
            img: it.img,
            searchTerm: it.searchTerm,
            position: it.position,
            frequency: it.frequency,
            domain: it.domain,
            gcs_url: it.gcs_url ?? it._gcs_uri_desktop ?? it._gcs_uri_mobile ?? null,
            _gcs_uri_desktop: it._gcs_uri_desktop ?? null,
            _gcs_uri_mobile: it._gcs_uri_mobile ?? null,
            device_type: it.device_type ?? null,
            brandInfringementTitle: it.brandInfringementTitle,
            brandInfringementDescription: it.brandInfringementDescription,
        } as PlaItem)), [AdItems]);
    const textAds: TextAdItem[] = useMemo(() => AdItems
        .filter((it: any) => (it.adType || 'PLA') === 'TEXT')
        .map((it: any) => ({
            domain: it.domain,
            title: it.title,
            description: it.description || '',
            position: it.position,
            frequency: it.frequency,
            searchTerm: it.searchTerm,
            gcs_url: it.gcs_url ?? it._gcs_uri_desktop ?? it._gcs_uri_mobile ?? null,
            _gcs_uri_desktop: it._gcs_uri_desktop ?? null,
            _gcs_uri_mobile: it._gcs_uri_mobile ?? null,
            device_type: it.device_type ?? null,
            brandInfringementTitle: it.brandInfringementTitle,
            brandInfringementDescription: it.brandInfringementDescription,
        } as TextAdItem)), [AdItems]);
    const rightPanelItems: PlaItem[] = useMemo(() => AdItems
        .filter((it: any) => (it.adType || 'PLA') === 'RightPanel')
        .map((it: any) => ({
            title: it.title,
            price: typeof it.price === 'string' ? it.price : String(it.price ?? ''),
            img: it.img,
            searchTerm: it.searchTerm,
            position: it.position,
            frequency: it.frequency,
            domain: it.domain,
            gcs_url: it.gcs_url ?? it._gcs_uri_desktop ?? it._gcs_uri_mobile ?? null,
            _gcs_uri_desktop: it._gcs_uri_desktop ?? null,
            _gcs_uri_mobile: it._gcs_uri_mobile ?? null,
            device_type: it.device_type ?? null,
            brandInfringementTitle: it.brandInfringementTitle,
            brandInfringementDescription: it.brandInfringementDescription,
        } as PlaItem)), [AdItems]);

    const pipedPlaItems = useMemo(() => runPipeline<PlaItem>({
        items: plaItems as PlaItem[],
        domain: selectedDomain,
        filterFn: (items) => applyStoreFiltersToPla(items as PlaItem[]),
        searchFn: (items) => searchPla(items as PlaItem[], searchQuery) as PlaItem[],
        sortFn: (items) => sortPlaItems(items as PlaItem[], sortBy, sortDirection) as PlaItem[],
    }), [plaItems, selectedDomain, applyStoreFiltersToPla, searchQuery, sortBy, sortDirection]);

    const pipedTextAds = useMemo(() => runPipeline<TextAdItem>({
        items: textAds as TextAdItem[],
        domain: selectedDomain,
        filterFn: (items) => applyStoreFiltersToText(items as TextAdItem[]),
        searchFn: (items) => searchTextAds(items as TextAdItem[], searchQuery) as TextAdItem[],
        sortFn: (items) => sortTextAds(items as TextAdItem[], sortBy, sortDirection) as TextAdItem[],
    }), [textAds, selectedDomain, applyStoreFiltersToText, searchQuery, sortBy, sortDirection]);

    const pipedRightPanelItems = useMemo(() => runPipeline<PlaItem>({
        items: rightPanelItems as PlaItem[],
        domain: selectedDomain,
        filterFn: (items) => applyStoreFiltersToRightPanel(items as PlaItem[]),
        searchFn: (items) => searchRightPanel(items as PlaItem[], searchQuery) as PlaItem[],
        sortFn: (items) => sortPlaItems(items as PlaItem[], sortBy, sortDirection) as PlaItem[],
    }), [rightPanelItems, selectedDomain, applyStoreFiltersToRightPanel, searchQuery, sortBy, sortDirection]);

    type Ad = {
        domain: string;
        [key: string]: unknown;
    };

    // Mémoriser le statut du filtre brand pour éviter les recalculs
    const brandFilterOn = useMemo(() =>
        Array.isArray(filterStore?.brandsPLA) && filterStore.brandsPLA.includes('__BRAND_INFRINGEMENT__'),
        [filterStore?.brandsPLA]
    );

    // Mémoriser les domaines flaggés pour éviter les recalculs coûteux
    const flaggedDomains = useMemo(() => {
        if (!brandFilterOn) return new Set<string>();

        const sources: Record<string, Ad[]> = {
            ALL: [...AdItems],
            PLA: [...plaItems],
            TEXT: textAds,
            RightPanel: rightPanelItems,
        } as any;
        const allAds = (sources as any)[adType] || sources.ALL;

        return new Set(
            allAds
                .filter((ad: any) => ad.brandInfringementTitle || ad.brandInfringementDescription)
                .map((ad: any) => ad.domain)
                .filter(Boolean)
        );
    }, [brandFilterOn, adType, AdItems, plaItems, textAds, rightPanelItems]);

    const filteredDomainItems = useMemo((): DomainItem[] => {
        if (!brandFilterOn) return domainItems;
        return domainItems.filter((d) => flaggedDomains.has(d.domain));
    }, [domainItems, brandFilterOn, flaggedDomains]);

    // Apply Drawer domains filter to the left panel list
    const leftPanelDomainItems = useMemo(() => {
        if (!domainsPLA || domainsPLA.length === 0) return filteredDomainItems;
        const setSel = new Set(domainsPLA);
        return filteredDomainItems.filter((d) => setSel.has(d.domain));
    }, [filteredDomainItems, domainsPLA]);

    // Mémoriser les domaines disponibles pour éviter les re-renders
    const availableDomains = useMemo(() =>
        leftPanelDomainItems.map(d => d.domain),
        [leftPanelDomainItems]
    );

    // Prefer selecting client's domain if available, otherwise the first domain
    const friendlyDomainsSet = useMemo(() => {
        const friendly = Array.from(new Set([
            ...(Array.isArray(workspace?.friendlyDomains) ? (workspace.friendlyDomains as string[]).filter(Boolean) : []),
            ...(workspace?.url ? [workspace.url] : []),
        ]
            .map((d: string) => normalizeDomain(d))
            .filter(Boolean)));
        return new Set(friendly);
    }, [workspace?.friendlyDomains, workspace?.url]);

    const preferredDomain = useMemo(() => {
        const firstFriendly = leftPanelDomainItems.find((d) => friendlyDomainsSet.has(normalizeDomain(d.domain)));
        return (firstFriendly?.domain || leftPanelDomainItems[0]?.domain || '').toString();
    }, [leftPanelDomainItems, friendlyDomainsSet]);

    const allDomainNames = useMemo(() => filteredDomainItems.map(d => d.domain), [filteredDomainItems]);

    // Keep selected domain in sync with available list and Drawer filter
    useEffect(() => {
        const hasAvailable = availableDomains.length > 0;
        const pending = deepLinkedDomainRef.current;

        // If a deep-linked domain is waiting but the Drawer filter hides it, clear the filter
        if (pending && !availableDomains.includes(pending) && domainsPLA.length > 0) {
            dispatch(setFilterDomainPLA([]));
            return;
        }

        // Deep-linked domain not found after data loaded (filter already cleared) → toast + fallback
        if (pending && !isLoadingAdsData && domainsPLA.length === 0 && allDomainNames.length > 0 && !allDomainNames.includes(pending)) {
            enqueueSnackbar(`Domain "${pending}" not found in ads data. Falling back to default.`, { variant: 'error' });
            deepLinkedDomainRef.current = null;
        }

        if (!hasAvailable) {
            if (selectedDomain && !pending) setSelectedDomain('');
            return;
        }

        // Priority: deep-linked domain > current selection > preferred (client) domain > first available
        let desired: string | null = null;
        if (pending && availableDomains.includes(pending)) {
            desired = pending;
            deepLinkedDomainRef.current = null;
        } else if (selectedDomain && availableDomains.includes(selectedDomain)) {
            desired = selectedDomain;
        } else if (preferredDomain && availableDomains.includes(preferredDomain)) {
            desired = preferredDomain;
        } else {
            desired = availableDomains[0];
        }

        if (desired && desired !== selectedDomain) {
            setSelectedDomain(desired);
        }

        // Sync Drawer domain filter with the effective selection
        if (desired && (domainsPLA.length !== 1 || domainsPLA[0] !== desired)) {
            dispatch(setFilterDomainPLA([desired]));
        }
    }, [availableDomains, preferredDomain, selectedDomain, domainsPLA, dispatch, isLoadingAdsData, allDomainNames]);

    // Apply adType filter to visible datasets, layout adapts based on these
    // Mémoriser les conditions de filtrage pour éviter les recalculs
    const shouldShowPla = useMemo(() => adType === 'ALL' || adType === 'PLA', [adType]);
    const shouldShowText = useMemo(() => adType === 'ALL' || adType === 'TEXT', [adType]);
    const shouldShowRight = useMemo(() => adType === 'ALL' || adType === 'RightPanel' || adType === 'PLA', [adType]);

    const visiblePlaItems = useMemo(
        () => shouldShowPla ? pipedPlaItems : [],
        [pipedPlaItems, shouldShowPla]
    );
    const visibleTextAds = useMemo(
        () => shouldShowText ? pipedTextAds : [],
        [pipedTextAds, shouldShowText]
    );
    const visibleRightPanelItems = useMemo(
        () => shouldShowRight ? pipedRightPanelItems : [],
        [pipedRightPanelItems, shouldShowRight]
    );

    // Presence-based layout flags (computed from visible datasets) - mémorisés
    const hasPla = useMemo(() => visiblePlaItems.length > 0, [visiblePlaItems.length]);
    const hasText = useMemo(() => visibleTextAds.length > 0, [visibleTextAds.length]);
    const hasRight = useMemo(() => visibleRightPanelItems.length > 0, [visibleRightPanelItems.length]);
    const onlyPla = useMemo(() => hasPla && !hasText && !hasRight, [hasPla, hasText, hasRight]);

    // Check if all visible datasets are empty
    const hasNoResults = useMemo(() => !hasPla && !hasText && !hasRight, [hasPla, hasText, hasRight]);
    // Single boolean to drive loading state for the view
    const isLoadingView = useMemo(() =>
        isLoadingCountries ||
        isLoadingAdsData ||
        (!selectedCountry && countriesData.length > 0) ||
        (!selectedDomain && leftPanelDomainItems.length > 0),
        [isLoadingCountries, isLoadingAdsData, selectedCountry, countriesData.length, selectedDomain, leftPanelDomainItems.length]
    );

    // Drawer requirements
    // Build advertiser counts based on available items (fallback on domain as seller identifier)

    // Callbacks mémorisés pour éviter les re-renders
    const handleDomainSelect = useCallback((d: string) => {
        setSelectedDomain(d);
        // Keep Drawer filter in sync with left selection
        const domains: string[] = (filterStore as any)?.domainsPLA || [];
        if (domains.length !== 1 || domains[0] !== d) {
            dispatch(setFilterDomainPLA([d]));
        }
    }, [filterStore, dispatch]);

    const handleCountryChange = useCallback((c: string | null) => {
        if (c) {
            try { ciContext?.setSelectedCountry?.(c); } catch { console.error("Error selecting country") }
        }
    }, [ciContext]);

    const handleSelectSearchTerm = useCallback((term: string) => {
        const currentKeywords = (filterStore?.keywords as string[] | undefined) || [];
        const isAlreadySelected = currentKeywords.includes(term);
        if (isAlreadySelected) {
            dispatch(setFilterKeyword(currentKeywords.filter((k) => k !== term)));
        } else {
            dispatch(setFilterKeyword([...currentKeywords, term]));
        }
    }, [dispatch, filterStore?.keywords]);

    const handleResetSearchTermFilter = useCallback(() => {
        dispatch(setFilterKeyword([]));
    }, [dispatch]);

    // Handlers for Drawer controls



    useEffect(() => {
        dispatch(setFilterDate({
            start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString()
        }))

        dispatch(setFilterCompare(false))

        dispatch(setFilterDateCompare({
            compareStart: subDays(new Date(), 60).toISOString(),
            compareEnd: subDays(new Date(), 31).toISOString(),
        }))
    }, [dispatch])

    return (

        <Box sx={{ overflowX: 'hidden', width: '100%' }} px={"24px"}>
            <Helmet>
                <title>Ads</title>
            </Helmet>
            <Box sx={{ mt: 2 }}>
                {countriesData.length === 0
                    ? <Box px={'24px'} mt={2} mb={2}>
                        <Skeleton width={'100%'} height={'100%'} style={{ borderRadius: 10 }} variant="rectangular" animation={"wave"}>
                            <>
                                <Iconify py={"auto"} icon={"eva:checkmark-circle-2-fill"} color={"success.main"} />
                                <Typography color={"success.main"}>Active</Typography>
                            </>
                        </Skeleton>
                    </Box>
                    : <Box pb={2}>
                        <CountryFilter
                            countries={countriesData}
                            selectedCountry={selectedCountry}
                            onCountryChange={handleCountryChange}
                            backgroundColor="transparent"
                        />
                    </Box>
                }
            </Box>
            <Paper sx={{ overflow: 'hidden', mt: 0, backgroundColor: 'transparent', position: 'relative' }}>
                {/* Main layout: three columns — left domain list | vertical divider | main content */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '280px 1px 1fr',
                        gap: 2,
                        height: 'calc(100vh - 265px)',
                        opacity: 1,
                        transform: 'translateY(0)',
                        transition: 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out',
                    }}
                >
                    <Box sx={{ gridColumn: '1', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <MemoLeftDomains
                            items={filteredDomainItems}
                            selected={selectedDomain}
                            onSelect={handleDomainSelect}
                            adType={adType}
                            selectedCountry={selectedCountry}
                        />
                    </Box>

                    {/* Vertical separator that stretches to touch the horizontal divider above */}
                    <Box sx={{ gridColumn: '2', bgcolor: 'divider', width: '1px', height: '100%' }} />

                    <Box sx={{ gridColumn: '3', minWidth: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden', pr: 1 }}>
                        {/* Top attacked search terms — skeleton on first load only, stays visible on keyword switches */}
                        <Box sx={{ pt: 2 }}>
                            {keywordItems.length === 0 || isFetchingKeywords
                                ? <Skeleton width={'100%'} height={60} variant="rectangular" animation="wave" sx={{ borderRadius: 1 }} />
                                : <TopAttackedSearchTerms terms={keywordItems} onSelectTerm={handleSelectSearchTerm} onResetFilter={handleResetSearchTermFilter} isClientDomain={friendlyDomainsSet.has(normalizeDomain(selectedDomain))} />
                            }
                        </Box>
                        {/* Ads content area — loading overlay only covers this zone */}
                        <Box sx={{ position: 'relative', minHeight: 200 }}>
                        {isLoadingView ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
                                    zIndex: 1,
                                }}
                            >
                                <AdsLoadingScreen externalLoadingState={isLoadingView} />
                            </Box>
                        ) : (
                            <>
                                {/* Show "0 results found" when no data is available */}
                                {hasNoResults ? (
                                    <Box sx={{ pt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', backgroundColor: 'background.neutral', maxWidth: '400px' }}>
                                            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                                                0 results found
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {t("dashboard.global.competitiveIntelligence.no_results_found_description")}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Top row: PLA section. Hidden when empty. If PLA is the only content on the page, switch to a responsive grid layout. */}
                                        {hasPla && (
                                            <Box sx={{ pt: 2 }}>
                                                <MemoPlaCarousel items={visiblePlaItems} layout={onlyPla ? 'grid' : 'carousel'} showBrandChip={brandFilterOn} />
                                            </Box>
                                        )}

                                        {/* Bottom row: Text / Right Panel depending on availability */}
                                        {hasText && hasRight && (
                                            /* Both present → two columns: Text (fluid) | Right panel (fixed 360px) */
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 360px', pt: 2, gap: 2 }}>
                                                <Box>
                                                    <MemoTextAdsList items={visibleTextAds} showBrandChip={brandFilterOn} />
                                                </Box>
                                                <Box>
                                                    <MemoRightPanelAds
                                                        items={visibleRightPanelItems}
                                                        showBrandChip={brandFilterOn}
                                                        totalCount={pipedRightPanelItems.length}
                                                    />
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Only Text present → full width */}
                                        {!hasRight && hasText && (
                                            <Box sx={{ pt: 2 }}>
                                                <MemoTextAdsList items={visibleTextAds} showBrandChip={brandFilterOn} />
                                            </Box>
                                        )}

                                        {/* Only Right panel present → full width, four cards per row */}
                                        {!hasText && hasRight && (
                                            <Box sx={{ pt: 2 }}>
                                                <MemoRightPanelAds
                                                    items={visibleRightPanelItems}
                                                    columns={4}
                                                    showBrandChip={brandFilterOn}
                                                    totalCount={pipedRightPanelItems.length}
                                                />
                                            </Box>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        </Box>
                    </Box>
                </Box>
            </Paper>
            {/* Filters Drawer */}
            <AdsFiltersDrawer open={filtersDrawerOpen} onClose={() => setFiltersDrawerOpen(false)} />
        </Box>
    );
}
